// src/pages/help/UpdateNotificationsTab.tsx

import React, { useState, useEffect } from "react";
import { Typography, Tag } from "antd";
import { BellOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchUserUpdateNotifications } from "../../api/updateNotifications/updateNotificationsApi";
import { UserUpdateNotification } from "../../types/communications/updateNotifications";
import UserUpdateNotificationModal from "./UserUpdateNotificationModal";
import { SectionCard, DataTable, EmptyState, InlineLoading } from "../../components/shared";
import { useNotifications } from "../../context/NotificationContext";

const { Text } = Typography;

const UpdateNotificationsTab: React.FC = () => {
	const { showError } = useNotifications();
	const [notifications, setNotifications] = useState<UserUpdateNotification[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState<UserUpdateNotification | null>(null);
	const [modalOpen, setModalOpen] = useState(false);

	useEffect(() => {
		loadNotifications();
	}, []);

	const loadNotifications = async () => {
		setLoading(true);
		try {
			const data = await fetchUserUpdateNotifications(true);
			setNotifications(data);
		} catch (error) {
			showError("Failed to load notifications");
		} finally {
			setLoading(false);
		}
	};

	const handleViewNotification = (notification: UserUpdateNotification) => {
		setSelectedNotification(notification);
		setModalOpen(true);
	};

	const handleDismiss = () => {
		setModalOpen(false);
		loadNotifications();
	};

	const handleClose = () => {
		setModalOpen(false);
	};

	const columns = [
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
		},
		{
			title: "Status",
			dataIndex: "read_at",
			key: "read_at",
			width: 100,
			render: (date: string | null, record: UserUpdateNotification) => {
				if (record.dismissed_at) return <Tag style={{ backgroundColor: "var(--mg-bg-subtle)", color: "var(--mg-text)" }}>Dismissed</Tag>;
				if (!date) return <Tag style={{ backgroundColor: "var(--mg-primary)", color: "white" }}>Unread</Tag>;
				return <Tag color="default">Read</Tag>;
			},
		},
		{
			title: "Date",
			dataIndex: "sent_at",
			key: "sent_at",
			width: 150,
			render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm"),
		},
	];

	return (
		<>
			<SectionCard noPadding>
				
				{loading && notifications.length === 0 ? (
					<InlineLoading minHeight={300} message="Loading notifications…" />
				) : notifications.length === 0 ? (
					<div style={{ padding: 20 }}>
						<EmptyState icon={<BellOutlined />} title="No notifications" description="You don't have any update notifications yet" />
					</div>
				) : (
					<DataTable columns={columns} dataSource={notifications} rowKey={(record) => record.id} onRowClick={(row) => handleViewNotification(row)} />
				)}
			</SectionCard>

			<UserUpdateNotificationModal notification={selectedNotification} open={modalOpen} onClose={handleClose} onDismiss={handleDismiss} />
		</>
	);
};

export default UpdateNotificationsTab;
